import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Shield, AlertTriangle, RotateCcw, FileArchive, Trash2, MailX, MailCheck, LogOut } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PageIntro from "../components/PageIntro";
import { toast } from "../components/ui/sonner";

/**
 * Profile & Privacy controls. Two flagship actions live here:
 *   1. Export — GDPR Art. 15 + 20. Streams a ZIP of every record we hold.
 *   2. Delete — GDPR Art. 17. Schedules deletion in 30 days, with a
 *      restore window during which the user can sign back in and cancel.
 */
export default function SettingsPage() {
  const { user, setUser, logout, logoutEverywhere } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [marketingBusy, setMarketingBusy] = useState(false);
  const [signingOutAll, setSigningOutAll] = useState(false);

  const marketingConsent = !!user?.marketing_consent;

  const handleToggleMarketing = async () => {
    if (marketingBusy) return;
    setMarketingBusy(true);
    try {
      const endpoint = marketingConsent
        ? "/me/marketing-unsubscribe"
        : "/me/marketing-resubscribe";
      await api.post(endpoint);
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success(
        marketingConsent
          ? "Unsubscribed from marketing emails"
          : "Subscribed to occasional product updates",
        {
          description: marketingConsent
            ? "You'll still receive transactional emails (reminders, password resets)."
            : "We'll send at most ~1 per month. Change your mind anytime here.",
        }
      );
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Could not update preference"
      );
    } finally {
      setMarketingBusy(false);
    }
  };

  const deletionPending = !!user?.deletion_pending;
  const eligibleAtIso = user?.deletion_eligible_at;
  const daysRemaining =
    eligibleAtIso
      ? Math.max(0, Math.ceil((new Date(eligibleAtIso) - new Date()) / 86400000))
      : null;

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    const t = toast.loading("Preparing your data export... this may take a moment.");
    try {
      const res = await api.get("/me/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0];
      a.download = `equia-export-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.dismiss(t);
      toast.success("Export ready", {
        description: "Your data archive has downloaded.",
      });
    } catch (err) {
      toast.dismiss(t);
      const msg = err.response?.data?.detail || "Export failed. Please try again later.";
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  const handleScheduleDelete = async () => {
    if (deleting) return;
    if (confirmText !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }
    setDeleting(true);
    try {
      const res = await api.delete("/me");
      toast.success("Account scheduled for deletion", {
        description: `You'll be permanently deleted on ${new Date(res.data.deletion_eligible_at).toLocaleDateString()}. Sign in within 30 days to cancel.`,
      });
      // Cookies were cleared server-side; force the AuthContext to reflect that.
      setUser(false);
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not schedule deletion");
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await api.post("/me/deletion/cancel");
      toast.success("Account deletion cancelled", {
        description: "Your data is safe. Welcome back.",
      });
      // Refresh user state so banner disappears
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel deletion");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white p-6 md:p-10 pb-24 md:pb-10">
      <PageIntro
        pageKey="settings"
        title="Profile & Privacy"
        subtitle="Two GDPR-grade controls live here — both immediate, both reversible (within a window)."
        bullets={[
          { icon: FileArchive, label: "Export my data", text: "Download a ZIP with every record EQUIA holds about you — portfolio, residencies, documents (decrypted), reminders." },
          { icon: Trash2, label: "Delete my account", text: "Schedules permanent deletion in 30 days. You can sign back in and cancel anytime during that window. After 30 days the data is unrecoverable." },
          { icon: Shield, label: "Sessions revoked instantly", text: "Deleting logs you out of every device at once. Restoring also forces a clean re-login." },
        ]}
      />
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] mb-2">
            Profile &amp; Privacy
          </p>
          <h1 className="font-serif text-3xl md:text-4xl text-white">Your account</h1>
          <p className="text-white/60 text-sm mt-2">
            Signed in as <span className="text-white/90">{user?.email}</span>
          </p>
        </div>

        {/* Pending-deletion banner */}
        {deletionPending && (
          <div
            className="mb-8 border border-[#e85d5d]/40 bg-[#e85d5d]/[0.05] p-5"
            data-testid="deletion-pending-banner"
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-[#e85d5d] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#e85d5d] mb-1">
                  Account scheduled for deletion
                </p>
                <p className="text-white text-sm leading-relaxed">
                  Your account and all data will be permanently deleted in{" "}
                  <strong>{daysRemaining} day{daysRemaining === 1 ? "" : "s"}</strong>
                  {eligibleAtIso && (
                    <> (on {new Date(eligibleAtIso).toLocaleDateString()})</>
                  )}
                  . Click below to restore your account.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCancelDelete}
              disabled={cancelling}
              data-testid="cancel-deletion-btn"
              className="bg-[#D4AF37] hover:bg-[#B5952F] text-black px-5 py-2.5 text-xs tracking-[0.15em] uppercase font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              {cancelling ? "Cancelling..." : "Restore my account"}
            </button>
          </div>
        )}

        {/* Marketing preferences — intentionally low-key */}
        <section
          className="border border-white/[0.10] bg-white/[0.01] px-6 py-4 mb-6 flex items-center justify-between gap-4"
          data-testid="marketing-section"
        >
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.2em] uppercase text-white/55 mb-0.5">
              Email preferences
            </p>
            <p className="text-white/85 text-sm">
              {marketingConsent
                ? "You're subscribed to occasional product updates."
                : "You're not subscribed to marketing emails."}
            </p>
            <p className="text-white/65 text-[11px] mt-1">
              Transactional emails (reminders, alerts, password resets) are not affected.
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggleMarketing}
            disabled={marketingBusy}
            data-testid={marketingConsent ? "marketing-unsubscribe-btn" : "marketing-resubscribe-btn"}
            className="flex-shrink-0 border border-white/15 hover:border-white/40 text-white/80 hover:text-white px-4 py-2 text-[11px] tracking-[0.15em] uppercase transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {marketingConsent ? <MailX className="w-3.5 h-3.5" /> : <MailCheck className="w-3.5 h-3.5" />}
            {marketingBusy
              ? "Saving…"
              : marketingConsent
                ? "Unsubscribe"
                : "Subscribe"}
          </button>
        </section>

        {/* Export section */}
        <section
          className="border border-white/[0.12] bg-white/[0.02] p-6 mb-6"
          data-testid="export-section"
        >
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 flex items-center justify-center bg-[#D4AF37]/[0.06] border border-[#D4AF37]/25 flex-shrink-0">
              <Download className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-[0.2em] uppercase text-white/60 mb-1">
                GDPR · Article 15 + 20
              </p>
              <h2 className="font-serif text-xl text-white">Export my data</h2>
              <p className="text-white/80 text-sm mt-2 leading-relaxed">
                Download a portable ZIP archive of every record EQUIA holds about
                you: your portfolio, residency entries, payment plans, encrypted
                documents (decrypted to plaintext), and account history. Right
                of access and portability under EU GDPR.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            data-testid="export-data-btn"
            className="bg-white/[0.04] hover:bg-white/[0.08] border border-white/15 text-white px-5 py-2.5 text-xs tracking-[0.15em] uppercase transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" />
            {exporting ? "Preparing archive..." : "Download my data"}
          </button>
          <p className="text-white/65 text-[11px] mt-3 leading-relaxed">
            The archive contains personal data — please store it securely. Large
            vaults can take a minute to assemble. Up to 5 requests per hour.
          </p>
        </section>

        {/* Delete section */}
        <section
          className="border border-[#e85d5d]/20 bg-[#e85d5d]/[0.02] p-6"
          data-testid="delete-section"
        >
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 flex items-center justify-center bg-[#e85d5d]/[0.06] border border-[#e85d5d]/25 flex-shrink-0">
              <Shield className="w-4 h-4 text-[#e85d5d]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-[0.2em] uppercase text-white/60 mb-1">
                GDPR · Article 17
              </p>
              <h2 className="font-serif text-xl text-white">Delete my account</h2>
              <p className="text-white/80 text-sm mt-2 leading-relaxed">
                Permanently delete your account and all associated data. We will
                stop email reminders immediately and revoke every active
                session. Your records will be kept for a{" "}
                <strong className="text-[#D4AF37]">30-day grace period</strong>{" "}
                during which you can sign back in and cancel. After that, your
                data is hard-deleted from our database and object storage and
                cannot be recovered.
              </p>
            </div>
          </div>

          {!deletionPending && !confirmDelete && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              data-testid="delete-account-btn"
              className="border border-[#e85d5d]/40 hover:bg-[#e85d5d]/10 text-[#e85d5d] px-5 py-2.5 text-xs tracking-[0.15em] uppercase transition-colors flex items-center gap-2"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              Delete my account
            </button>
          )}

          {!deletionPending && confirmDelete && (
            <div className="mt-4 border-t border-white/[0.10] pt-4">
              <p className="text-white text-sm mb-3">
                To confirm, type <strong className="text-[#e85d5d] font-mono">DELETE</strong> below:
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                data-testid="delete-confirm-input"
                placeholder="DELETE"
                className="w-full max-w-xs bg-[#050505] border border-white/15 text-white px-4 py-2.5 text-sm font-mono mb-3 focus:border-[#e85d5d] focus:ring-1 focus:ring-[#e85d5d] outline-none"
              />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleScheduleDelete}
                  disabled={deleting || confirmText !== "DELETE"}
                  data-testid="confirm-delete-btn"
                  className="bg-[#e85d5d] hover:bg-[#d34a4a] text-black px-5 py-2.5 text-xs tracking-[0.15em] uppercase font-medium transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {deleting ? "Scheduling..." : "Schedule deletion"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmDelete(false);
                    setConfirmText("");
                  }}
                  data-testid="cancel-delete-confirm-btn"
                  className="text-white/65 hover:text-white text-[11px] tracking-[0.15em] uppercase transition-colors px-3 py-2.5"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Sign out */}
        <div className="mt-10 flex flex-col items-center gap-4">
          <button
            type="button"
            disabled={signingOutAll}
            onClick={async () => {
              if (signingOutAll) return;
              setSigningOutAll(true);
              try {
                const data = await logoutEverywhere();
                const revoked = data?.revoked ?? 0;
                toast.success(
                  revoked > 0
                    ? `Signed out of ${revoked} session${revoked === 1 ? "" : "s"}`
                    : "Signed out of all devices"
                );
                navigate("/");
              } catch (err) {
                toast.error("Could not sign out of all devices — you have been signed out locally");
                navigate("/");
              } finally {
                setSigningOutAll(false);
              }
            }}
            data-testid="settings-signout-everywhere-btn"
            className="group flex items-center gap-2 text-white/70 hover:text-white text-[11px] tracking-[0.15em] uppercase transition-colors disabled:opacity-50"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
            {signingOutAll ? "Signing out everywhere…" : "Sign out of all devices"}
          </button>

          <button
            type="button"
            onClick={() => logout()}
            data-testid="settings-signout-btn"
            className="text-white/50 hover:text-white/80 text-[11px] tracking-[0.15em] uppercase transition-colors"
          >
            Sign out (this device)
          </button>
        </div>
      </div>
    </div>
  );
}
